import {
  CustomFieldProps,
  DynamicField,
  DynamicForm,
  FieldVariant,
  SelectFieldProps,
  SelectOption,
  TextFieldProps,
  NumberFieldProps,
  TextareaFieldProps,
} from '@/components/form-builder/form-builder.types';
import { MultiFormAccordionComponent } from '@/components/multi-form-accordion/multi-form-accordion.component';
import { DacGeneralParamsRepository } from '@/store/dac-general-params-state/dac-general-params-state.repository';
import { DynaDocsModelStatusFormComponent } from '../../dyna-docs-model-status-form/dyna-docs-model-status-form.component';
import { ParamGeneralType } from '@/types';
import { map } from 'rxjs';
import { systemTypeOptions, systemSubTypes } from '@/store/dac-general-params-state/param-general-field-state/param-general-field.repository';

interface GetEditDacGeneralParamsFormObjectProps {
  store: DacGeneralParamsRepository;
  dynaDocsModelOptions?: SelectOption[];
}

export const getEditDacGeneralParamsFormObject = ({ store, dynaDocsModelOptions }: GetEditDacGeneralParamsFormObjectProps): DynamicForm => {
  const typeOptions: SelectOption[] = Object.values(ParamGeneralType).map(value => ({
    name: `dacGeneralParams.types.${value}`,
    code: value,
  }));

  const isNotSystemType$ = store.getObservable<SelectOption>('paramGeneralTypeSelected').pipe(map(type => type?.code !== ParamGeneralType.SYSTEM));

  const currentSystemSubTypeOptions$ = store.getObservable<SelectOption>('sourceSelected').pipe(
    map(source => {
      if (!source?.code) return [];
      const key = source.code as string;
      return (systemSubTypes[key] || []).map(st => ({ name: st.label, code: st.value }));
    })
  );

  const paramGeneralNameField: DynamicField<TextFieldProps> = {
    label: 'dacGeneralParams.form.name',
    variant: FieldVariant.TEXT,
    isRequired: true,
    error: store.getNestedObservable<string>('errors', 'paramGeneralName'),
    props: {
      placeholder: 'dacGeneralParams.form.namePlaceholder',
      value: store.getNestedObservable('updateDto', 'paramGeneralName'),
      onChange: (value: string) => {
        store.setNested('updateDto', 'paramGeneralName', value);
        store.setNested('errors', 'paramGeneralName', '');
      },
    },
  };

  const paramGeneralLabelField: DynamicField<TextFieldProps> = {
    label: 'dacGeneralParams.form.label',
    variant: FieldVariant.TEXT,
    isRequired: true,
    error: store.getNestedObservable<string>('errors', 'paramGeneralLabel'),
    props: {
      placeholder: 'dacGeneralParams.form.labelPlaceholder',
      value: store.getNestedObservable('updateDto', 'paramGeneralLabel'),
      onChange: (value: string) => {
        store.setNested('updateDto', 'paramGeneralLabel', value);
        store.setNested('errors', 'paramGeneralLabel', '');
      },
    },
  };

  const paramGeneralTypeField: DynamicField<SelectFieldProps> = {
    label: 'dacGeneralParams.form.type',
    variant: FieldVariant.SELECT,
    isRequired: true,
    error: store.getNestedObservable<string>('errors', 'paramGeneralType'),
    props: {
      placeholder: 'dacGeneralParams.form.typePlaceholder',
      options: typeOptions,
      value: store.getObservable('paramGeneralTypeSelected'),
      onSelectChange: (value: SelectOption) => {
        store.setNested('updateDto', 'paramGeneralType', value.code as ParamGeneralType);
        store.set('paramGeneralTypeSelected', value);
        store.setNested('errors', 'paramGeneralType', '');
        if (value?.code !== ParamGeneralType.SYSTEM) {
          store.set('sourceSelected', { name: '', code: undefined });
          store.set('sourceFieldSelected', { name: '', code: undefined });
          store.setNested('updateDto', 'paramGeneralSource', '');
          store.setNested('updateDto', 'paramGeneralSourceField', '');
        }
      },
    },
  };

  const paramGeneralOrderField: DynamicField<NumberFieldProps> = {
    label: 'dacGeneralParams.form.order',
    variant: FieldVariant.NUMBER,
    isRequired: true,
    error: store.getNestedObservable<string>('errors', 'paramGeneralOrder'),
    props: {
      placeholder: 'dacGeneralParams.form.orderPlaceholder',
      value: store.getNestedObservable('updateDto', 'paramGeneralOrder'),
      onChange: (value: number) => {
        store.setNested('updateDto', 'paramGeneralOrder', value);
        store.setNested('errors', 'paramGeneralOrder', '');
      },
    },
  };

  const paramGeneralDescriptionField: DynamicField<TextareaFieldProps> = {
    label: 'dacGeneralParams.field.description',
    variant: FieldVariant.TEXTAREA,
    props: {
      placeholder: 'dacGeneralParams.field.descriptionPlaceholder',
      value: store.getNestedObservable('updateDto', 'paramGeneralDescription'),
      onChange: (value: string) => {
        store.setNested('updateDto', 'paramGeneralDescription', value);
      },
      rows: 5,
      resize: 'none',
    },
  };

  const sourceField: DynamicField<SelectFieldProps> = {
    label: 'dynamicDocument.form.nameSource',
    variant: FieldVariant.SELECT,
    isRequired: true,
    isHidden: isNotSystemType$,
    error: store.getNestedObservable<string>('errors', 'paramGeneralSource'),
    props: {
      placeholder: 'dynamicDocument.form.placeholders.nameSource',
      value: store.getObservable<SelectOption>('sourceSelected'),
      onSelectChange: (option: SelectOption) => {
        store.set('sourceSelected', option);
        store.setNested('updateDto', 'paramGeneralSource', option?.code || '');
        store.set('sourceFieldSelected', { name: '', code: undefined });
        store.setNested('updateDto', 'paramGeneralSourceField', '');
        store.setNested('errors', 'paramGeneralSource', '');
      },
      options: systemTypeOptions,
    },
  };

  const sourceFieldField: DynamicField<SelectFieldProps> = {
    label: 'dynamicDocument.form.fields',
    variant: FieldVariant.SELECT,
    isRequired: true,
    isHidden: isNotSystemType$,
    error: store.getNestedObservable<string>('errors', 'paramGeneralSourceField'),
    props: {
      placeholder: 'dynamicDocument.form.placeholders.sourceField',
      value: store.getObservable<SelectOption>('sourceFieldSelected'),
      onSelectChange: (option: SelectOption) => {
        store.set('sourceFieldSelected', option);
        store.setNested('updateDto', 'paramGeneralSourceField', option?.code || '');
        store.setNested('errors', 'paramGeneralSourceField', '');
      },
      options: currentSystemSubTypeOptions$,
    },
  };

  const modelsAccordion: DynamicField<CustomFieldProps> = {
    label: 'dacGeneralParams.models.title',
    variant: FieldVariant.CUSTOM,
    props: {
      component: MultiFormAccordionComponent,
      injectKeys: ['accordionObject'],
      accordionObject: {
        titles: ['dacGeneralParams.models.singular'],
        component: DynaDocsModelStatusFormComponent,
        injectKeys: ['dynaDocsModelOptions'],
        dynaDocsModelOptions,
      },
    },
  };

  return {
    title: 'dacGeneralParams.singular',
    description: '',
    isHeaderHidden: true,
    grids: [
      {
        title: '',
        isHeaderHidden: true,
        gridItems: [
          { fields: [paramGeneralNameField, paramGeneralLabelField] },
          { fields: [paramGeneralTypeField, paramGeneralOrderField] },
          { fields: [sourceField, sourceFieldField] },
          { fields: [paramGeneralDescriptionField] },
          { fields: [modelsAccordion] },
        ],
      },
    ],
  };
};
